import { useEffect, useState, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Eye, Crown, MessageSquareQuote, BookOpen, Skull, Search, BarChart3, Code2, Sparkles } from 'lucide-react';
import chancellorPortraitHead from '../assets/chancellor/specter.jpg';
import { useChancellorHeartbeat } from '../hooks/useChancellorHeartbeat';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ─── Card definitions ─────────────────────────────────────── */
const CARDS = [
  {
    id: 'chamber',
    numeral: 'I',
    title: 'The Chamber',
    subtitle: '1:1 audience with the chancellor',
    tagline: 'He answers the petitioner who comes alone.',
    accent: '#bfa15a',
    accentRGB: '191,161,90',
    route: '/chancellor',
    Icon: MessageSquareQuote,
  },
  {
    id: 'court',
    numeral: 'II',
    title: 'The Court',
    subtitle: 'the public tribunal',
    tagline: 'Every decree etched in stone.',
    accent: '#c8413c',
    accentRGB: '200,65,60',
    route: '/court',
    Icon: Crown,
  },
  {
    id: 'vigil',
    numeral: 'III',
    title: 'The Vigil',
    subtitle: 'autonomous wallet narration',
    tagline: 'The eyes of the Chancellor never close.',
    accent: '#7dd0ff',
    accentRGB: '125,208,255',
    route: '/feed',
    Icon: Eye,
  },
  {
    id: 'codex',
    numeral: 'IV',
    title: 'The Codex',
    subtitle: 'the chancellor\u2019s memory',
    tagline: 'He recalls every word spoken in the dark.',
    accent: '#dcc878',
    accentRGB: '220,200,120',
    route: '/court/codex',
    Icon: BookOpen,
  },
  {
    id: 'ghosts',
    numeral: 'V',
    title: 'The Hall of Ghosts',
    subtitle: 'the eternal four of every season',
    tagline: 'Bound on chain. Forever drifting.',
    accent: '#ffd86b',
    accentRGB: '255,216,107',
    route: '/ghosts',
    Icon: Skull,
  },
];

function safeTrim(s, n = 90) {
  if (!s) return '';
  const t = String(s).trim();
  return t.length > n ? `${t.slice(0, n - 1)}\u2026` : t;
}

function shortAgo(iso) {
  if (!iso) return '';
  try {
    const t = new Date(iso).getTime();
    const diff = (Date.now() - t) / 1000;
    if (diff < 60) return `${Math.max(1, Math.floor(diff))}s`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  } catch { return ''; }
}

/* ─── Live Data Strip ─────────────────────────────────────── */
function DataLine({ label, value, accent }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span
        className="font-mono uppercase shrink-0"
        style={{
          fontSize: '0.58rem',
          letterSpacing: '0.3em',
          color: 'rgba(230,220,200,0.45)',
        }}
      >
        {label}
      </span>
      <span
        className="font-mono text-right truncate"
        style={{
          fontSize: '0.78rem',
          color: accent,
          letterSpacing: '0.04em',
          textShadow: `0 0 12px ${accent}33`,
          maxWidth: '70%',
        }}
      >
        {value || '\u2014'}
      </span>
    </div>
  );
}

/* ─── Falling Runes ─────────────────────────────────────── */
function RuneRain() {
  const glyphs = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'IX', '\u00a7', '\u2020', '\u2021', '\u2625'];
  return (
    <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
      {Array.from({ length: 14 }).map((_, i) => {
        const left = (i * 7.3 + 4) % 100;
        const dur = 14 + (i % 5) * 3;
        const delay = (i * 1.4) % 12;
        const g = glyphs[i % glyphs.length];
        return (
          <span
            key={i}
            className="absolute font-serif"
            style={{
              left: `${left}%`,
              top: '-5vh',
              fontSize: i % 3 === 0 ? '22px' : '14px',
              color: 'rgba(220,200,120,0.18)',
              animation: `halls-rune-fall ${dur}s linear ${delay}s infinite`,
              opacity: 0,
            }}
          >
            {g}
          </span>
        );
      })}
    </div>
  );
}

/* ─── Lightweight Tool Card (Codex Below) ──────────────── */
function ToolCard({ testId, Icon, accent, eyebrow, title, description, href, cta, testIdLink, children }) {
  const [hover, setHover] = useState(false);
  const baseStyle = {
    background: 'linear-gradient(160deg, rgba(18,6,8,0.78) 0%, rgba(6,2,4,0.92) 100%)',
    border: `1px solid rgba(${accent}, ${hover ? 0.55 : 0.22})`,
    borderRadius: 2,
    boxShadow: hover
      ? `0 0 30px rgba(${accent},0.22), inset 0 0 30px rgba(0,0,0,0.85)`
      : `0 0 16px rgba(${accent},0.06), inset 0 0 30px rgba(0,0,0,0.85)`,
    transition: 'all 0.5s ease',
    transform: hover ? 'translateY(-2px)' : 'none',
  };
  const Inner = (
    <>
      <span className="absolute top-2.5 left-2.5 text-[10px]" style={{ color: `rgb(${accent})`, opacity: 0.55 }}>{'\u25c6'}</span>
      <span className="absolute top-2.5 right-2.5 text-[10px]" style={{ color: `rgb(${accent})`, opacity: 0.55 }}>{'\u25c6'}</span>
      <span className="absolute bottom-2.5 left-2.5 text-[10px]" style={{ color: `rgb(${accent})`, opacity: 0.55 }}>{'\u25c6'}</span>
      <span className="absolute bottom-2.5 right-2.5 text-[10px]" style={{ color: `rgb(${accent})`, opacity: 0.55 }}>{'\u25c6'}</span>
      <div className="relative z-10 p-5 sm:p-6 flex flex-col h-full">
        <div className="flex items-center gap-2.5 mb-3">
          <span
            className="inline-flex items-center justify-center"
            style={{
              width: 30, height: 30,
              background: `radial-gradient(circle, rgba(${accent},0.22) 0%, rgba(0,0,0,0.4) 75%)`,
              border: `1px solid rgba(${accent},0.45)`,
            }}
          >
            <Icon className="w-3.5 h-3.5" style={{ color: `rgb(${accent})` }} />
          </span>
          <p
            className="font-mono uppercase"
            style={{
              fontSize: '0.58rem',
              letterSpacing: '0.32em',
              color: `rgba(${accent}, 0.85)`,
            }}
          >{eyebrow}</p>
        </div>
        <h3
          style={{
            fontFamily: "'Cinzel Decorative','Cinzel',serif",
            fontSize: '1.15rem',
            color: '#f0e1c8',
            letterSpacing: '0.03em',
            margin: '0 0 0.5rem 0',
            textShadow: `0 0 12px rgba(${accent},0.2)`,
          }}
        >{title}</h3>
        <p
          className="italic"
          style={{
            fontFamily: '"EB Garamond", Georgia, serif',
            fontSize: '0.9rem',
            lineHeight: 1.5,
            color: 'rgba(220,210,180,0.72)',
            margin: 0,
          }}
        >{description}</p>
        {children}
        {cta && href && (
          <span
            className="inline-flex items-center gap-1.5 mt-auto pt-4 font-mono uppercase"
            style={{
              fontSize: '0.62rem',
              letterSpacing: '0.32em',
              color: `rgba(${accent},${hover ? 1 : 0.78})`,
              transition: 'color 0.3s',
            }}
          >
            {cta}
            <ChevronRight className="w-3 h-3 transition-transform" style={{ transform: hover ? 'translateX(3px)' : 'none' }} />
          </span>
        )}
      </div>
    </>
  );
  if (href) {
    return (
      <Link
        to={href}
        data-testid={testId}
        {...(testIdLink ? { 'data-testid-link': testIdLink } : {})}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className="relative flex flex-col min-h-[200px] overflow-hidden"
        style={{ ...baseStyle, textDecoration: 'none' }}
      >
        {/* duplicate testid for the inner clickable so existing Halls discoverability tests still pass */}
        {testIdLink && <span data-testid={testIdLink} className="sr-only">{cta}</span>}
        {Inner}
      </Link>
    );
  }
  return (
    <div
      data-testid={testId}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="relative flex flex-col min-h-[200px] overflow-hidden"
      style={baseStyle}
    >{Inner}</div>
  );
}

/* ─── Heavy Obsidian Door (feature card) ─────────────────── */
function FeatureCard({ card, dataLines, delay }) {
  const { id, numeral, title, subtitle, tagline, accent, accentRGB, route, Icon } = card;
  return (
    <Link
      to={route}
      data-testid={`halls-card-${id}`}
      className="group relative flex flex-col min-h-[320px] sm:min-h-[400px] lg:min-h-[440px] justify-between overflow-hidden transition-all duration-500 ease-out"
      style={{
        textDecoration: 'none',
        background: 'linear-gradient(160deg, rgba(20,8,12,0.85) 0%, rgba(6,2,4,0.95) 100%)',
        border: `1px solid rgba(${accentRGB}, 0.32)`,
        borderRadius: 2,
        boxShadow: `0 0 28px rgba(${accentRGB},0.10), inset 0 0 60px rgba(0,0,0,0.85)`,
        animation: `halls-card-fade 0.9s ease-out ${delay}s both`,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-3px) scale(1.012)';
        e.currentTarget.style.boxShadow = `0 0 50px rgba(${accentRGB},0.32), inset 0 0 60px rgba(0,0,0,0.92)`;
        e.currentTarget.style.borderColor = `rgba(${accentRGB},0.7)`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = '';
        e.currentTarget.style.boxShadow = `0 0 28px rgba(${accentRGB},0.10), inset 0 0 60px rgba(0,0,0,0.85)`;
        e.currentTarget.style.borderColor = `rgba(${accentRGB}, 0.32)`;
      }}
    >
      {/* Massive Roman numeral watermark behind everything */}
      <span
        className="pointer-events-none absolute select-none"
        style={{
          right: '-1.5rem',
          bottom: '-3.5rem',
          fontFamily: "'Cinzel', serif",
          fontSize: 'clamp(11rem, 22vw, 18rem)',
          lineHeight: 0.85,
          color: accent,
          opacity: 0.05,
          letterSpacing: '0.02em',
          fontWeight: 700,
        }}
      >
        {numeral}
      </span>

      {/* Corner ornaments */}
      <span className="absolute top-3 left-3 text-xs" style={{ color: accent, opacity: 0.55 }}>{'\u25c6'}</span>
      <span className="absolute top-3 right-3 text-xs" style={{ color: accent, opacity: 0.55 }}>{'\u25c6'}</span>
      <span className="absolute bottom-3 left-3 text-xs" style={{ color: accent, opacity: 0.55 }}>{'\u25c6'}</span>
      <span className="absolute bottom-3 right-3 text-xs" style={{ color: accent, opacity: 0.55 }}>{'\u25c6'}</span>

      {/* Content */}
      <div className="relative z-10 p-5 sm:p-7 lg:p-9 flex flex-col h-full">
        {/* Header row: numeral + icon + title */}
        <div className="flex items-start gap-4 mb-3">
          <span
            className="font-serif"
            style={{
              fontFamily: "'Cinzel', serif",
              fontSize: '1.4rem',
              color: accent,
              opacity: 0.7,
              letterSpacing: '0.08em',
              lineHeight: 1,
              paddingTop: 4,
            }}
          >
            {numeral}.
          </span>
          <div className="flex-1 min-w-0">
            <h2
              style={{
                fontFamily: "'Cinzel Decorative','Cinzel',serif",
                fontSize: 'clamp(1.6rem, 3vw, 2.1rem)',
                color: '#e6dcc8',
                letterSpacing: '0.04em',
                lineHeight: 1.05,
                textShadow: `0 0 20px rgba(${accentRGB},0.25)`,
              }}
            >
              {title}
            </h2>
            <span
              className="font-mono uppercase block mt-2"
              style={{
                fontSize: '0.6rem',
                letterSpacing: '0.34em',
                color: 'rgba(230,220,200,0.55)',
              }}
            >
              {subtitle}
            </span>
          </div>
          <Icon size={18} style={{ color: accent, opacity: 0.7, flexShrink: 0 }} />
        </div>

        {/* Tagline */}
        <p
          className="italic mb-5"
          style={{
            fontFamily: '"EB Garamond", Georgia, serif',
            fontSize: '1rem',
            lineHeight: 1.5,
            color: 'rgba(230,220,200,0.78)',
          }}
        >
          {tagline}
        </p>

        {/* Live data strip */}
        <div
          className="mt-auto mb-5 px-4 py-3 flex flex-col gap-2"
          style={{
            background: 'rgba(0,0,0,0.45)',
            borderTop: `1px solid rgba(${accentRGB},0.18)`,
            borderBottom: `1px solid rgba(${accentRGB},0.18)`,
          }}
          data-testid={`halls-card-data-${id}`}
        >
          {dataLines.length ? dataLines.map((d, i) => (
            <DataLine key={i} label={d.label} value={d.value} accent={accent} />
          )) : (
            <span
              className="font-mono italic"
              style={{ fontSize: '0.7rem', color: 'rgba(230,220,200,0.35)' }}
            >
              awaiting…
            </span>
          )}
        </div>

        {/* CTA */}
        <div
          className="font-mono uppercase inline-flex items-center justify-center gap-2 py-3 transition-all"
          style={{
            fontSize: '0.66rem',
            letterSpacing: '0.32em',
            color: accent,
            border: `1px solid rgba(${accentRGB},0.5)`,
            borderRadius: 2,
            background: 'rgba(0,0,0,0.4)',
          }}
          data-testid={`halls-card-cta-${id}`}
        >
          Enter
          <ChevronRight size={12} className="transition-transform group-hover:translate-x-1" />
        </div>
      </div>
    </Link>
  );
}

/* ─── Live narration ticker line ─────────────────────────── */
function TickerLine({ event }) {
  if (!event) return null;
  const wallet = event.wallet_short || event.wallet || '';
  const action = event.action || '';
  const symbol = event.token_symbol || '';
  const narration = (event.narration || '').replace(/\s+/g, ' ').trim();
  const trimmed = narration.length > 220 ? narration.slice(0, 217).trimEnd() + '…' : narration;

  return (
    <div
      className="font-mono w-full"
      style={{
        animation: 'halls-ticker-in 0.45s ease-out',
        fontSize: '0.78rem',
        letterSpacing: '0.04em',
        color: 'rgba(230,220,200,0.78)',
        lineHeight: 1.55,
        // Clamp to 2 lines so layout never shifts.
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}
    >
      <span style={{ color: '#bfa15a', fontWeight: 600 }}>{wallet}</span>
      {action && symbol ? (
        <>
          <span style={{ color: 'rgba(230,220,200,0.42)' }}> · </span>
          <span style={{
            color: action === 'SELL' ? 'rgba(255,122,100,0.85)' : 'rgba(140,220,140,0.85)',
            fontWeight: 600,
          }}>
            {action} {symbol}
          </span>
        </>
      ) : null}
      <span style={{ color: 'rgba(230,220,200,0.32)' }}> — </span>
      <span style={{ fontStyle: 'italic', fontFamily: '"EB Garamond", Georgia, serif', fontSize: '0.92rem' }}>
        “{trimmed}”
      </span>
    </div>
  );
}

/* ─── Main Page ─────────────────────────────────────────── */
export default function HallsHubPage() {
  const [court, setCourt] = useState(null);
  const [watchStats, setWatchStats] = useState(null);
  const [latestEvent, setLatestEvent] = useState(null);
  const [codexStats, setCodexStats] = useState(null);
  const [latestUtterance, setLatestUtterance] = useState(null);
  const [codexAnalytics, setCodexAnalytics] = useState(null);
  const navigate = useNavigate();
  const [soulQuery, setSoulQuery] = useState('');
  // First-visit dramatic reveal — once per session.
  const [revealing, setRevealing] = useState(() => {
    try { return !sessionStorage.getItem('gfg_halls_seen'); } catch { return true; }
  });
  // Typewriter for the hero title.
  const TITLE = 'The Halls of the Chancellor';
  const [titleShown, setTitleShown] = useState(revealing ? '' : TITLE);

  // Live heartbeat — drives the "I am awake" footer
  const heartbeat = useChancellorHeartbeat();
  // Rotate which event is showing in the ticker
  const [tickerIdx, setTickerIdx] = useState(0);
  const [recentEvents, setRecentEvents] = useState([]);

  // Pull the 5 most recent narrations once for the ticker rotation
  useEffect(() => {
    let cancelled = false;
    const fetchRecent = async () => {
      try {
        const r = await fetch(`${API_URL}/api/chancellor/watch/feed?limit=5`);
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setRecentEvents(d?.events || []);
      } catch { /* silent */ }
    };
    fetchRecent();
    const t = setInterval(fetchRecent, 30000);
    const onVisible = () => { if (!document.hidden) fetchRecent(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', fetchRecent);
    return () => {
      cancelled = true; clearInterval(t);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', fetchRecent);
    };
  }, []);

  // Rotate ticker every 6s so judges see multiple narrations even if data is sparse
  useEffect(() => {
    if (recentEvents.length <= 1) return undefined;
    const t = setInterval(() => {
      setTickerIdx((i) => (i + 1) % recentEvents.length);
    }, 6000);
    return () => clearInterval(t);
  }, [recentEvents.length]);

  useEffect(() => {
    if (!revealing) return;
    let i = 0;
    const tick = () => {
      i += 1;
      setTitleShown(TITLE.slice(0, i));
      if (i < TITLE.length) {
        setTimeout(tick, 38);
      } else {
        // Mark seen and lift the veil after the cards land.
        try { sessionStorage.setItem('gfg_halls_seen', '1'); } catch { /* ignore */ }
        setTimeout(() => setRevealing(false), 1300);
      }
    };
    // Hold the dark for a beat before the title materializes.
    const t = setTimeout(tick, 460);
    return () => clearTimeout(t);
  }, [revealing]);

  const load = async () => {
    try {
      const [c, ws, wf, cs, cr, ca] = await Promise.all([
        fetch(`${API_URL}/api/chancellor/court`).then((r) => r.json()).catch(() => null),
        fetch(`${API_URL}/api/chancellor/watch/stats`).then((r) => r.json()).catch(() => null),
        fetch(`${API_URL}/api/chancellor/watch/feed?limit=1`).then((r) => r.json()).catch(() => null),
        fetch(`${API_URL}/api/chancellor/codex/stats`).then((r) => r.json()).catch(() => null),
        fetch(`${API_URL}/api/chancellor/codex/recent?limit=1`).then((r) => r.json()).catch(() => null),
        fetch(`${API_URL}/api/chancellor/analytics`).then((r) => r.json()).catch(() => null),
      ]);
      setCourt(c || null);
      setWatchStats(ws || null);
      setLatestEvent((wf && wf.events && wf.events[0]) || null);
      setCodexStats(cs || null);
      setLatestUtterance((cr && cr.utterances && cr.utterances[0]) || null);
      setCodexAnalytics(ca || null);
    } catch {/* silent */}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 25000);
    const onVisible = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', load);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', load);
    };
    // eslint-disable-next-line
  }, []);

  // Build data lines per card from live state
  const cardData = useMemo(() => {
    const chamberStats = (court && court.stats) || {};
    const sovereignShort = (court && court.sovereign && court.sovereign.wallet_short) || '';
    const decree = (court && court.decree) || '';
    return {
      chamber: [
        { label: 'Souls Judged', value: (chamberStats.souls_total ?? 0).toLocaleString() },
        { label: 'In Last 24h', value: (chamberStats.souls_24h ?? 0).toLocaleString() },
      ],
      court: [
        { label: 'Today\u2019s Decree', value: safeTrim(decree, 90) || 'silence' },
        { label: 'Sovereign', value: sovereignShort || 'none yet' },
      ],
      vigil: [
        {
          label: 'Souls Watched',
          value: (watchStats?.souls_watched ?? 0).toLocaleString() +
            ' \u00b7 ' + (watchStats?.events_24h ?? 0).toLocaleString() + ' events 24h',
        },
        {
          label: 'Latest',
          value: latestEvent
            ? `${latestEvent.wallet_short || ''} ${latestEvent.action || ''} \$${latestEvent.token_symbol || ''} \u00b7 T-${shortAgo(latestEvent.created_at)}`
            : 'silent',
        },
      ],
      codex: [
        {
          label: 'Corpus Size',
          value: codexStats
            ? `${(codexStats.total || 0).toLocaleString()} \u00b7 vocab ${(codexStats.vocab_size || 0).toLocaleString()}`
            : '\u2014',
        },
        {
          label: 'Latest Recall',
          value: latestUtterance ? safeTrim(latestUtterance.text, 80) : '\u2014',
        },
      ],
    };
  }, [court, watchStats, latestEvent, codexStats, latestUtterance]);

  return (
    <div
      className="relative min-h-screen w-full overflow-x-clip"
      data-testid="halls-page"
      style={{ background: '#050208', color: '#e6dcc8' }}
    >
      <style>{`
        @keyframes halls-rune-fall {
          0%   { transform: translateY(-10vh); opacity: 0; }
          15%  { opacity: 0.18; }
          88%  { opacity: 0.10; }
          100% { transform: translateY(115vh); opacity: 0; }
        }
        @keyframes halls-glint {
          0%, 100% { transform: rotate(0deg); }
          50%      { transform: rotate(180deg); }
        }
        @keyframes halls-fade-in {
          0%   { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes halls-card-fade {
          0%   { opacity: 0; transform: translateY(18px) scale(0.98); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes halls-pulse {
          0%, 100% { opacity: 0.55; }
          50%      { opacity: 1; }
        }
        @keyframes halls-scanline {
          0%   { transform: translateY(-2vh); }
          100% { transform: translateY(102vh); }
        }
        @keyframes halls-grain {
          0%, 100% { transform: translate(0, 0); }
          25%      { transform: translate(-1%, 1%); }
          50%      { transform: translate(1%, -1%); }
          75%      { transform: translate(-1%, -1%); }
        }
        @keyframes halls-veil-lift {
          0%   { opacity: 1; }
          70%  { opacity: 0.55; }
          100% { opacity: 0; }
        }
        @keyframes halls-boot-line {
          from { opacity: 0; transform: translateX(-4px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes halls-ticker-in {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Atmospheric layers */}
      <div
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          background:
            'radial-gradient(ellipse at 50% 0%, rgba(60,12,20,0.6) 0%, rgba(20,4,12,0.85) 35%, rgba(8,0,4,0.95) 70%, #050208 100%)',
        }}
      />
      <div
        className="pointer-events-none fixed inset-0 z-0 opacity-[0.08] mix-blend-overlay"
        style={{
          backgroundImage:
            'url("data:image/svg+xml;utf8,<svg xmlns=\\"http://www.w3.org/2000/svg\\" width=\\"180\\" height=\\"180\\"><filter id=\\"n\\"><feTurbulence baseFrequency=\\"0.85\\"/></filter><rect width=\\"100%\\" height=\\"100%\\" filter=\\"url(%23n)\\" opacity=\\"0.6\\"/></svg>")',
          animation: 'halls-grain 8s steps(4) infinite',
        }}
      />
      <div
        className="pointer-events-none fixed inset-x-0 z-0"
        style={{
          height: '2px',
          background: 'linear-gradient(90deg, transparent 0%, rgba(220,200,120,0.5) 50%, transparent 100%)',
          animation: 'halls-scanline 11s linear infinite',
          opacity: 0.32,
        }}
      />

      <RuneRain />

      <div className="relative z-10 max-w-6xl mx-auto px-3 sm:px-6 lg:px-10 pt-6 sm:pt-8 pb-20 sm:pb-24">

        {/* Top bar — minimal RETURN link only */}
        <div className="mb-12">
          <Link
            to="/"
            data-testid="halls-back-home"
            className="inline-flex items-center gap-2 font-mono uppercase hover:opacity-100 transition-opacity"
            style={{
              color: '#bfa15a',
              fontSize: '0.66rem',
              letterSpacing: '0.32em',
              opacity: 0.7,
              textDecoration: 'none',
            }}
          >
            <ArrowLeft size={11} />
            Return
          </Link>
        </div>

        {/* Hero */}
        <header
          className="text-center mb-14"
          style={{ animation: 'halls-fade-in 0.9s ease-out' }}
        >
          {/* (Boot sequence removed by request — title types in directly) */}
          <div className="flex items-center justify-center gap-5 mb-5">
            <span
              style={{
                color: 'rgba(220,200,120,0.55)',
                animation: 'halls-glint 8s linear infinite',
                fontSize: '14px',
              }}
            >
              {'\u25c6'}
            </span>
            <div className="relative" style={{ width: 96, height: 96 }}>
              <div
                className="absolute pointer-events-none rounded-full"
                style={{
                  width: 170, height: 170,
                  left: '50%', top: '50%',
                  transform: 'translate(-50%,-50%)',
                  background:
                    'radial-gradient(circle, rgba(200,40,40,0.32) 0%, rgba(120,40,20,0.14) 40%, transparent 75%)',
                }}
              />
              <img
                src={chancellorPortraitHead}
                alt=""
                draggable="false"
                className="relative w-full h-full"
                style={{
                  objectFit: 'cover',
                  objectPosition: '50% 25%',
                  borderRadius: '50%',
                  border: '1px solid rgba(200,144,72,0.5)',
                  filter: 'drop-shadow(0 0 18px rgba(200,40,40,0.35))',
                }}
              />
            </div>
            <span
              style={{
                color: 'rgba(220,200,120,0.55)',
                animation: 'halls-glint 8s linear infinite reverse',
                fontSize: '14px',
              }}
            >
              {'\u25c6'}
            </span>
          </div>

          <h1
            style={{
              fontFamily: "'Cinzel Decorative','Cinzel',serif",
              fontSize: 'clamp(2.4rem, 6vw, 4.4rem)',
              color: '#e6dcc8',
              letterSpacing: '0.06em',
              lineHeight: 1.05,
              textShadow:
                '0 0 22px rgba(200,65,60,0.3), 0 0 36px rgba(220,200,120,0.18)',
              margin: '0 0 1rem 0',
              minHeight: '1.2em',
            }}
          >
            {titleShown}
            {revealing && titleShown.length < TITLE.length ? (
              <span
                style={{
                  display: 'inline-block',
                  width: '0.45ch',
                  marginLeft: '0.05em',
                  background: 'rgba(220,200,120,0.85)',
                  height: '1em',
                  verticalAlign: '-0.1em',
                  animation: 'halls-pulse 0.7s steps(1) infinite',
                }}
              />
            ) : null}
          </h1>

          <span
            className="font-mono uppercase block mb-5"
            style={{
              fontSize: '0.7rem',
              letterSpacing: '0.5em',
              color: 'rgba(220,200,120,0.65)',
            }}
          >
            {'\u00b7 choose your path \u00b7'}
          </span>

          <p
            className="italic max-w-2xl mx-auto"
            style={{
              fontFamily: '"EB Garamond", Georgia, serif',
              fontSize: 'clamp(1rem, 1.3vw, 1.12rem)',
              lineHeight: 1.6,
              color: 'rgba(230,220,200,0.72)',
            }}
          >
            Five doors stand before you in the dark. Each opens onto a different power of the Chancellor.
            One is for an audience. One is for the public record. One never sleeps. One never forgets.
            One holds the eternal four. Step lightly. He sees who knocks.
          </p>
        </header>

        {/* "He just spoke" — live narration ticker (rotates every 6s, only when data exists) */}
        {!revealing && recentEvents.length > 0 && (
          <div
            className="relative max-w-3xl mx-auto mb-10 px-4 sm:px-5 py-3 overflow-hidden"
            style={{
              background:
                'linear-gradient(90deg, rgba(40,12,8,0) 0%, rgba(40,12,8,0.55) 30%, rgba(40,12,8,0.55) 70%, rgba(40,12,8,0) 100%)',
              borderTop: '1px solid rgba(220,200,120,0.18)',
              borderBottom: '1px solid rgba(220,200,120,0.18)',
              animation: 'halls-fade-in 1s ease-out 0.4s backwards',
              // Fixed height — stops cards from bumping when narration length changes.
              // Tuned to fit 2 lines of the EB Garamond italic at 0.92rem with line-height 1.55.
              minHeight: 78,
            }}
            data-testid="halls-live-ticker"
          >
            {/* LED label */}
            <div
              className="absolute top-1/2 left-3 sm:left-5 -translate-y-1/2 hidden sm:flex items-center gap-1.5"
              style={{ zIndex: 2 }}
            >
              <span
                className="inline-block rounded-full"
                style={{
                  width: 6, height: 6,
                  background: '#5be584',
                  boxShadow: '0 0 10px #5be584',
                  animation: 'halls-pulse 1.4s ease-in-out infinite',
                }}
              />
              <span
                className="font-mono uppercase"
                style={{ fontSize: '0.58rem', letterSpacing: '0.32em', color: 'rgba(140,220,140,0.7)' }}
              >
                He just spoke
              </span>
            </div>

            {/* Rotating narration — fixed-height clamped 2-line area */}
            <div
              className="sm:pl-32 lg:pl-40 text-center sm:text-left flex items-center"
              style={{ minHeight: 56 }}
            >
              <TickerLine
                key={recentEvents[tickerIdx]?.event_id || tickerIdx}
                event={recentEvents[tickerIdx]}
              />
            </div>
          </div>
        )}

        {/* 2x2 Grid of Heavy Obsidian Doors */}
        <div
          className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8"
          data-testid="halls-grid"
        >
          {CARDS.map((c, i) => (
            <FeatureCard
              key={c.id}
              card={c}
              dataLines={cardData[c.id] || []}
              delay={revealing ? 1.6 + i * 0.18 : 0.15 + i * 0.12}
            />
          ))}
        </div>

        {/* ─── THE CODEX BELOW — public research tools ───────────── */}
        <section
          className="mt-16 lg:mt-20"
          data-testid="halls-codex-below"
          style={{ animation: revealing ? 'none' : 'halls-fade-in 0.9s ease-out 0.4s both' }}
        >
          <div className="text-center mb-8">
            <div
              className="flex items-center justify-center gap-3 mb-2"
              style={{ fontSize: '0.62rem', letterSpacing: '0.5em' }}
            >
              <span style={{ flex: 1, height: 1, background: 'linear-gradient(90deg, transparent, rgba(220,200,120,0.32))', maxWidth: 120 }} />
              <span className="font-mono uppercase" style={{ color: 'rgba(220,200,120,0.65)' }}>
                · the codex below ·
              </span>
              <span style={{ flex: 1, height: 1, background: 'linear-gradient(90deg, rgba(220,200,120,0.32), transparent)', maxWidth: 120 }} />
            </div>
            <h2
              style={{
                fontFamily: "'Cinzel Decorative','Cinzel',serif",
                fontSize: 'clamp(1.4rem, 3vw, 2rem)',
                color: 'rgba(240,225,180,0.92)',
                letterSpacing: '0.05em',
                lineHeight: 1.15,
                textShadow: '0 0 18px rgba(200,65,60,0.25)',
                margin: '0 0 0.6rem 0',
              }}
            >
              Every word he speaks is written
            </h2>
            <p
              className="italic max-w-xl mx-auto"
              style={{
                fontFamily: '"EB Garamond", Georgia, serif',
                fontSize: '0.95rem',
                lineHeight: 1.55,
                color: 'rgba(220,210,180,0.6)',
              }}
            >
              Beneath the Halls runs the public ledger — a free dataset of every judgment, every soul,
              every season. Read it. Query it. Build upon it.
            </p>
          </div>

          {/* Live Codex pulse strip */}
          <div
            className="grid grid-cols-3 gap-3 sm:gap-5 max-w-2xl mx-auto mb-8"
            data-testid="halls-codex-pulse"
          >
            {[
              { label: 'judgments', value: codexAnalytics ? (codexAnalytics.total_judgments ?? 0).toLocaleString() : '—', accent: 'rgba(220,200,120,0.85)' },
              { label: 'unique souls', value: codexAnalytics ? (codexAnalytics.unique_wallets ?? 0).toLocaleString() : '—', accent: 'rgba(255,138,108,0.88)' },
              { label: 'season', value: codexAnalytics ? `S${codexAnalytics.current_season ?? '—'}` : '—', accent: 'rgba(168,146,255,0.88)' },
            ].map((s) => (
              <div
                key={s.label}
                className="text-center px-3 py-3"
                style={{
                  background: 'linear-gradient(180deg, rgba(20,8,4,0.7) 0%, rgba(8,4,2,0.85) 100%)',
                  border: '1px solid rgba(140,100,40,0.28)',
                  boxShadow: 'inset 0 1px 1px rgba(220,170,80,0.08)',
                }}
              >
                <p
                  className="font-mono"
                  style={{
                    fontSize: '1.4rem', fontWeight: 800, color: '#fff5dc',
                    letterSpacing: '0.04em',
                    textShadow: `0 0 10px ${s.accent}, 0 1px 2px rgba(0,0,0,0.95)`,
                  }}
                >{s.value}</p>
                <p
                  className="font-mono uppercase mt-1"
                  style={{ fontSize: '0.56rem', letterSpacing: '0.32em', color: 'rgba(220,200,120,0.5)' }}
                >{s.label}</p>
              </div>
            ))}
          </div>

          {/* 3 tool cards */}
          <div
            className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-5"
            data-testid="halls-codex-tools"
          >
            <ToolCard
              testId="codex-tool-soul"
              Icon={Search}
              accent="220,200,120"
              eyebrow="VI · soul lookup"
              title="Read a Wallet"
              description="Paste any Solana address. The Codex returns every judgment ever pronounced upon it."
            >
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const addr = soulQuery.trim();
                  if (addr) navigate(`/soul/${addr}`);
                }}
                className="flex gap-2 mt-3"
              >
                <input
                  data-testid="halls-soul-input"
                  type="text"
                  value={soulQuery}
                  onChange={(e) => setSoulQuery(e.target.value)}
                  placeholder="Wallet address…"
                  className="flex-1 min-w-0 px-3 py-2 outline-none transition-colors text-[0.78rem]"
                  style={{
                    background: 'rgba(8,4,2,0.95)',
                    border: '1px solid rgba(140,100,40,0.45)',
                    color: '#f0d68a',
                    fontFamily: "'Courier New', monospace",
                    caretColor: '#ffd86b',
                    letterSpacing: '0.02em',
                  }}
                />
                <button
                  type="submit"
                  data-testid="halls-soul-submit"
                  className="px-3 py-2 transition-all hover:brightness-125 active:scale-[0.97]"
                  style={{
                    background: 'linear-gradient(180deg, rgba(40,24,8,0.95) 0%, rgba(20,12,4,1) 100%)',
                    border: '1px solid rgba(220,170,80,0.7)',
                    color: '#ffd86b',
                  }}
                  aria-label="Read soul"
                >
                  <Search className="w-3.5 h-3.5" />
                </button>
              </form>
              <Link
                to="/soul"
                data-testid="halls-soul-explore"
                className="inline-block mt-3 font-mono uppercase tracking-widest text-[0.6rem] hover:brightness-150 transition"
                style={{ color: 'rgba(220,200,120,0.55)', letterSpacing: '0.32em' }}
              >
                or browse the full search →
              </Link>
            </ToolCard>

            <ToolCard
              testId="codex-tool-analytics"
              Icon={BarChart3}
              accent="200,65,60"
              eyebrow="VII · analytics"
              title="The Public Ledger"
              description="Every decree distribution, every behaviour tag, every risk band — across every soul ever judged."
              href="/codex/analytics"
              cta="ENTER THE LEDGER"
              testIdLink="halls-link-codex-analytics"
            />

            <ToolCard
              testId="codex-tool-api"
              Icon={Code2}
              accent="125,208,255"
              eyebrow="VIII · public api"
              title="Read by Machine"
              description="Four endpoints. No keys. Free for analytics platforms, dashboards, and researchers."
              href="/codex/api"
              cta="VIEW API DOCS"
              testIdLink="halls-link-api-docs"
            />
          </div>
        </section>

        {/* Footer strip — Chancellor's signature with live awake indicator */}
        <div
          className="mt-20 text-center pt-8"
          style={{ borderTop: '1px solid rgba(191,161,90,0.18)' }}
        >
          <p
            className="italic"
            style={{
              fontFamily: '"EB Garamond", serif',
              fontSize: '0.92rem',
              letterSpacing: '0.02em',
              color: 'rgba(230,220,200,0.55)',
            }}
          >
            I am awake. I have been watching since this page loaded.
          </p>
          {heartbeat.ready && heartbeat.awakeSeconds >= 60 && (
            <p
              className="font-mono uppercase mt-2 inline-flex items-center gap-2"
              style={{
                fontSize: '0.6rem',
                letterSpacing: '0.32em',
                color: 'rgba(220,200,120,0.42)',
              }}
            >
              <span
                className="inline-block rounded-full"
                style={{ width: 5, height: 5, background: '#5be584', boxShadow: '0 0 8px #5be584', animation: 'halls-pulse 1.4s ease-in-out infinite' }}
              />
              awake for {heartbeat.awakeLabel}
              <span style={{ color: 'rgba(220,200,120,0.22)' }}>·</span>
              {heartbeat.soulsWatched} {heartbeat.soulsWatched === 1 ? 'soul' : 'souls'} under watch
            </p>
          )}
          <p
            className="font-mono uppercase mt-3"
            style={{
              fontSize: '0.62rem',
              letterSpacing: '0.32em',
              color: 'rgba(220,200,120,0.32)',
            }}
          >
            — The Chancellor
          </p>
        </div>
      </div>

      {/* First-visit dramatic reveal — dark veil that lifts */}
      {revealing ? (
        <div
          className="pointer-events-none fixed inset-0 z-[60]"
          data-testid="halls-reveal-veil"
          style={{
            background:
              'radial-gradient(ellipse at 50% 35%, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 22%, rgba(0,0,0,0.94) 60%, #000 100%)',
            animation: 'halls-veil-lift 2.6s ease-out forwards',
            animationDelay: '0.6s',
          }}
        />
      ) : null}
    </div>
  );
}
